import { Component, ElementRef, HostListener, ViewChild, AfterViewInit, inject } from '@angular/core';
import { MenuService } from '../../services/menu.service';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SvgIconComponent } from 'angular-svg-icon';
import { FocusManagementService } from '../../services/focus-management.service';
import { SkipLinksService } from '../../services/skip-links.service';

/**
 * Navigation bar component with mobile menu and accessibility features.
 *
 * @remarks Provides the main navigation interface for the application with
 * responsive design support, focus management, and keyboard accessibility.
 * Includes a collapsible phone number display with proper focus trapping
 * and keyboard navigation support.
 */
@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, SvgIconComponent],
})
export class NavbarComponent implements AfterViewInit {
  /** @private Skip links service for accessibility navigation */
  private skipLinksService = inject(SkipLinksService);

  /** @remarks Signal indicating whether a skip link is currently focused */
  isSkipLinkFocused = this.skipLinksService.isSkipLinkFocused;

  /** @remarks Flag controlling the visibility of the phone number dropdown */
  showPhoneNumber = false;

  /** @remarks Reference to the phone number container element */
  @ViewChild('phoneNumberContainer') phoneNumberContainer!: ElementRef;

  /** @remarks Reference to the phone number link element */
  @ViewChild('phoneNumberLink') phoneNumberLink!: ElementRef;

  /** @private First focusable element in the phone number container for focus trapping */
  private firstFocusableElement: HTMLElement | null = null;

  /** @private Last focusable element in the phone number container for focus trapping */
  private lastFocusableElement: HTMLElement | null = null;

  /**
   * Creates an instance of NavbarComponent.
   *
   * @param menu - Service for managing mobile menu state
   * @param elementRef - Reference to the component's DOM element
   * @param focusService - Service for managing focus and keyboard navigation
   */
  constructor(
    public menu: MenuService,
    private elementRef: ElementRef,
    private focusService: FocusManagementService
  ) {}

  /**
   * Lifecycle hook called after the component's view has been initialized.
   *
   * @remarks Updates focusable elements after the view is fully rendered
   * to ensure proper focus management for accessibility features.
   */
  ngAfterViewInit(): void {
    // Initialize focusable elements when the component initializes
    setTimeout(() => this.updateFocusableElements(), 0);
  }

  /**
   * Toggles the visibility of the phone number dropdown.
   *
   * @remarks Manages focus state when opening and closing the phone number display.
   * Saves current focus before opening and sets focus to the first element
   * in the dropdown, or restores previous focus when closing.
   */
  togglePhoneNumber(): void {
    if (!this.showPhoneNumber) {
      // Save the last focused element before opening
      this.focusService.saveCurrentFocus();
    }

    this.showPhoneNumber = !this.showPhoneNumber;

    if (this.showPhoneNumber) {
      // Focus the first focusable element in the phone number container
      setTimeout(() => {
        this.updateFocusableElements();
        this.focusService.focusFirstElement(this.phoneNumberContainer);
      }, 100);
    } else {
      // Restore focus to the last focused element
      this.focusService.restoreFocus();
    }
  }

  /**
   * Closes the phone number dropdown.
   *
   * @remarks Safely closes the phone number display and restores focus
   * to the previously focused element. Does nothing if the dropdown
   * is already closed.
   */
  closePhoneNumber(): void {
    if (!this.showPhoneNumber) return;

    this.showPhoneNumber = false;
    this.focusService.restoreFocus();
  }

  /**
   * Updates the first and last focusable elements for focus trapping.
   *
   * @remarks Refreshes the references to focusable elements within the
   * phone number container to ensure proper keyboard navigation and
   * focus trapping functionality.
   */
  updateFocusableElements(): void {
    if (this.phoneNumberContainer) {
      const elements = this.focusService.getFocusableElements(this.phoneNumberContainer);
      this.firstFocusableElement = elements.first;
      this.lastFocusableElement = elements.last;
    }
  }

  /**
   * Handles document click events for closing the phone number dropdown.
   *
   * @remarks Closes the phone number dropdown when clicking outside of
   * the phone number container or contact button. Implements proper
   * click-outside behavior for the dropdown interface.
   *
   * @param event - The mouse click event
   */
  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent): void {
    if (!this.showPhoneNumber) {
      return;
    }

    const clickedInside =
      this.elementRef.nativeElement.querySelector('.phone-number-container')?.contains(event.target as Node) ||
      this.elementRef.nativeElement.querySelector('.contact')?.contains(event.target as Node);

    if (!clickedInside) {
      this.closePhoneNumber();
    }
  }

  /**
   * Handles document keydown events for keyboard navigation.
   *
   * @remarks Implements keyboard accessibility for the phone number dropdown.
   * Handles Escape key to close the dropdown and Tab key navigation
   * with proper focus trapping within the container.
   *
   * @param event - The keyboard event
   */
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.showPhoneNumber) {
      return;
    }

    // Close phone number when escape key is pressed
    if (event.key === 'Escape') {
      this.closePhoneNumber();
      event.preventDefault();
    }

    // Handle tab key navigation to trap focus within the phone number container
    if (event.key === 'Tab') {
      if (!this.firstFocusableElement || !this.lastFocusableElement) {
        this.updateFocusableElements();
      }

      this.focusService.handleTabKey(event, this.firstFocusableElement, this.lastFocusableElement, () => this.closePhoneNumber());
    }
  }
}
